import { useMemo } from 'react'
import { useTheme, alpha } from '@mui/material/styles'
import EChart from './EChart'
import { POOL_COLORS, fmtCredits, fmtPool } from './vocabulary'

const tooltipStyle = {
  backgroundColor: 'rgba(33, 33, 41, 0.94)',
  borderWidth: 0,
  textStyle: { color: '#fff', fontSize: 12 },
}

// The whole account's projection, drawn as dashed outlines in even steps to
// the cycle's own total at the reset (`cost.py` `projected_credits`, never a
// second one made up here). Like that total it steps from the last day
// ElevenLabs reported, so the days since, today among them, carry an outline
// over their own bar; before any report it steps from this environment's
// meter, as the total does.
const projection = (series, projected) => {
  let from = series.findLastIndex(({ reported }) => reported != null)
  if (from < 0) from = series.findLastIndex(({ ours }) => ours != null)
  const ahead = series.length - 1 - from
  if (from < 0 || !ahead || projected == null) return series.map(() => null)
  const base = series[from].reported ?? series[from].ours
  const step = (projected - base) / ahead
  return series.map((row, index) =>
    index > from ? Math.round(base + step * (index - from)) : null
  )
}

// The cycle day by day, cumulative: this environment's own metered credits,
// and the rest of what ElevenLabs reports for the account (manual clips and
// the other environments), which is known only through the last complete
// reported day. The dashed line is an even pace to the included credits.
// Plotted in dollars at the contract's rate (`cycle.credit_usd`), with the
// credits in the tooltip; in credits from an API that sends no rate.
const PoolChart = ({ cycle, height = 240 }) => {
  const theme = useTheme()
  const series = useMemo(() => cycle?.series || [], [cycle])
  const option = useMemo(() => {
    const text = theme.palette.text.secondary
    const grid = alpha(text, 0.15)
    const ahead = projection(series, cycle?.projected_credits)
    const rate = cycle?.credit_usd
    const plot = (credits) =>
      credits == null || rate == null ? credits : credits * rate
    const axis =
      rate == null
        ? fmtCredits
        : (usd) => `$${Math.round(usd).toLocaleString('en-US')}`
    const figure = (credits) => fmtPool(credits, cycle)
    return {
      grid: { left: 52, right: 16, top: 20, bottom: 24 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...tooltipStyle,
        formatter: (points) => {
          const index = points[0].dataIndex
          const row = series[index]
          if (row.ours == null)
            return `${row.day} UTC<br/>projected, whole account ${figure(
              ahead[index]
            )} · even pace ${figure(row.pace)}`
          const theirs =
            row.reported == null ? null : Math.max(0, row.reported - row.ours)
          return `<div style="min-width:220px">
            <div style="margin-bottom:4px">${row.day} UTC</div>
            <div>This environment's streams <b>${figure(row.ours)}</b></div>
            <div>Everything else <b>${
              theirs == null ? 'not reported yet' : figure(theirs)
            }</b></div>
            ${
              ahead[index] == null
                ? ''
                : `<div>Projected, whole account <b>${figure(
                    ahead[index]
                  )}</b></div>`
            }
            <div style="opacity:.75;margin-top:4px">Even pace ${figure(
              row.pace
            )}</div>
          </div>`
        },
      },
      xAxis: {
        type: 'category',
        data: series.map(({ day }) => day),
        axisLabel: { color: text, fontSize: 10, formatter: (d) => d.slice(5) },
        axisLine: { lineStyle: { color: grid } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: (extent) =>
          Math.max(extent.max, plot(cycle?.credits_limit || 0) * 1.08),
        axisLabel: { color: text, fontSize: 10, formatter: axis },
        splitLine: { lineStyle: { color: grid } },
      },
      series: [
        {
          name: "This environment's streams",
          type: 'bar',
          stack: 'pool',
          color: POOL_COLORS.ours,
          barMaxWidth: 18,
          data: series.map(({ ours }) => plot(ours)),
          markLine: {
            symbol: 'none',
            silent: true,
            lineStyle: { color: text },
            label: {
              formatter: `${axis(plot(cycle?.credits_limit || 0))} included`,
              color: text,
              fontSize: 10,
              position: 'insideEndTop',
            },
            data: [{ yAxis: plot(cycle?.credits_limit || 0) }],
          },
        },
        {
          name: 'Manual clips and other environments',
          type: 'bar',
          stack: 'pool',
          color: POOL_COLORS.theirs,
          barMaxWidth: 18,
          data: series.map(({ ours, reported }) =>
            reported == null || ours == null
              ? null
              : plot(Math.max(0, reported - ours))
          ),
        },
        // Over the stack rather than on it: `barGap` puts it in the same
        // slot, so a day with a bar keeps it inside the outline.
        {
          name: 'Projected, whole account',
          type: 'bar',
          barMaxWidth: 18,
          barGap: '-100%',
          itemStyle: {
            color: 'transparent',
            borderColor: text,
            borderType: 'dashed',
            borderWidth: 1.5,
          },
          data: ahead.map(plot),
        },
        {
          name: 'Even pace',
          type: 'line',
          symbol: 'none',
          lineStyle: { color: text, type: 'dashed', width: 1.5 },
          data: series.map(({ pace }) => plot(pace)),
        },
      ],
    }
  }, [series, cycle, theme])

  return (
    <EChart
      option={option}
      height={height}
      isEmpty={!series.length}
      emptyText="No cycle to show yet."
    />
  )
}

export default PoolChart
